import { post } from "@/utils/api-handler";

export interface UploadError {
  error_code?: string;
  message?: string;
  details?: string;
}

export interface UploadTask {
  id: string;
  file: File;
  datasourceId: string;
  groupId: string;
  description?: string;
  status: "pending" | "uploading" | "completed" | "failed" | "paused";
  progress: number;
  trackingId?: string;
  currentChunk: number;
  totalChunks: number;
  error?: string;
  createdAt: Date;
  onError?: (error: UploadError) => void;
}

export interface UploadResponse {
  tracking_id: string;
  chunk_size: number;
  total_chunks: number;
}

class BackgroundUploadService {
  private static instance: BackgroundUploadService;
  private uploadTasks: Map<string, UploadTask> = new Map();
  private activeUploads: Set<string> = new Set();
  private listeners: Set<(tasks: UploadTask[]) => void> = new Set();
  private chunkSize = 3 * 1024 * 1024; // Exactly 1MB chunks
  private maxConcurrentUploads = 1; // Increased to 3 files at a time
  private maxConcurrentChunks = 5; // Upload 3 chunks in parallel

  private constructor() {
    // Load existing tasks from localStorage on initialization
    this.loadTasksFromStorage();

    // Resume any pending uploads
    this.resumePendingUploads();
  }

  static getInstance(): BackgroundUploadService {
    if (!BackgroundUploadService.instance) {
      BackgroundUploadService.instance = new BackgroundUploadService();
    }
    return BackgroundUploadService.instance;
  }

  // Add a new upload task
  addUpload(
    file: File,
    datasourceId: string,
    groupId: string,
    description?: string,
    onError?: (error: UploadError) => void
  ): string {
    const taskId = this.generateTaskId();
    const task: UploadTask = {
      id: taskId,
      file,
      datasourceId,
      groupId,
      description,
      status: "pending",
      progress: 0,
      currentChunk: 0,
      totalChunks: 0,
      createdAt: new Date(),
      onError,
    };

    this.uploadTasks.set(taskId, task);
    this.saveTasksToStorage();
    this.notifyListeners();

    // Start upload immediately if we have capacity
    this.processQueue();

    return taskId;
  }

  // Subscribe to upload progress updates
  subscribe(callback: (tasks: UploadTask[]) => void): () => void {
    this.listeners.add(callback);
    // Immediately call with current state
    callback(Array.from(this.uploadTasks.values()));

    return () => {
      this.listeners.delete(callback);
    };
  }

  // Get all upload tasks
  getAllTasks(): UploadTask[] {
    return Array.from(this.uploadTasks.values()).sort(
      (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
    );
  }

  // Get task by ID
  getTask(taskId: string): UploadTask | undefined {
    return this.uploadTasks.get(taskId);
  }

  // Cancel an upload
  cancelUpload(taskId: string): void {
    const task = this.uploadTasks.get(taskId);
    if (task) {
      task.status = "failed";
      task.error = "Cancelled by user";
      this.activeUploads.delete(taskId);
      this.saveTasksToStorage();
      this.notifyListeners();
      this.processQueue();
    }
  }

  // Retry a failed upload
  retryUpload(taskId: string): void {
    const task = this.uploadTasks.get(taskId);
    if (task && task.status === "failed") {
      task.status = "pending";
      task.progress = 0;
      task.currentChunk = 0;
      task.error = undefined;
      task.trackingId = undefined;
      this.saveTasksToStorage();
      this.notifyListeners();
      this.processQueue();
    }
  }

  // Remove completed or failed uploads
  removeTask(taskId: string): void {
    this.uploadTasks.delete(taskId);
    this.activeUploads.delete(taskId);
    this.saveTasksToStorage();
    this.notifyListeners();
  }

  // Clear all completed uploads
  clearCompleted(): void {
    const completedTasks = Array.from(this.uploadTasks.entries())
      .filter(([_, task]) => task.status === "completed")
      .map(([id]) => id);

    completedTasks.forEach((id) => this.uploadTasks.delete(id));
    this.saveTasksToStorage();
    this.notifyListeners();
  }

  // Clear all tasks and localStorage
  clearAll(): void {
    this.uploadTasks.clear();
    this.activeUploads.clear();
    localStorage.removeItem("backgroundUploads");
    this.notifyListeners();
  }

  private async processQueue(): Promise<void> {
    const pendingTasks = Array.from(this.uploadTasks.values())
      .filter((task) => task.status === "pending")
      .slice(0, this.maxConcurrentUploads - this.activeUploads.size);

    for (const task of pendingTasks) {
      if (this.activeUploads.size >= this.maxConcurrentUploads) break;
      this.startUpload(task);
    }
  }

  private async startUpload(task: UploadTask): Promise<void> {
    this.activeUploads.add(task.id);
    task.status = "uploading";
    this.notifyListeners();

    try {
      // Auth is handled server-side by /api/[...path] via httpOnly cookies.
      // Step 1: Initiate upload if not already done
      let initResponse: UploadResponse;
      if (!task.trackingId) {
        initResponse = await this.initiateUpload(task);
        task.trackingId = initResponse.tracking_id;
        task.totalChunks = initResponse.total_chunks;
        this.saveTasksToStorage();

        // Reduced delay from 3000ms to 500ms
        const initDelayMs = 500;
        await new Promise(resolve => setTimeout(resolve, initDelayMs));
      } else {
        // If we already have tracking ID, we need to reconstruct the response
        initResponse = {
          tracking_id: task.trackingId,
          chunk_size: this.chunkSize,
          total_chunks: task.totalChunks,
        };
      }

      // Step 2: Upload chunks in parallel (faster)
      await this.uploadChunksInParallel(task, initResponse);

      task.status = "completed";
      task.progress = 100;
      this.saveTasksToStorage();
      this.notifyListeners();
    } catch (error: any) {
      task.status = "failed";
      task.error = error.message;
      this.saveTasksToStorage();
      this.notifyListeners();

      // Call the error callback if provided
      if (task.onError) {
        task.onError({
          error_code: error.error_code || "UPLOAD_ERROR",
          message: error.message || "Upload failed",
          details: error.details,
        });
      }
    } finally {
      this.activeUploads.delete(task.id);
      this.processQueue();
    }
  }

  private async uploadChunksInParallel(
    task: UploadTask,
    initResponse: UploadResponse
  ): Promise<void> {
    let completedChunks = task.currentChunk;

    // Upload chunks in batches
    for (let batchStart = completedChunks; batchStart < task.totalChunks; batchStart += this.maxConcurrentChunks) {
      // Check if task was cancelled
      if (task.status !== "uploading") {
        return;
      }

      // Create batch of chunk uploads
      const batchPromises: Promise<void>[] = [];
      const batchEnd = Math.min(batchStart + this.maxConcurrentChunks, task.totalChunks);

      for (let chunkNumber = batchStart; chunkNumber < batchEnd; chunkNumber++) {
        const chunk = await this.getChunk(
          task.file,
          chunkNumber,
          initResponse.chunk_size
        );
        const isFinalChunk = chunkNumber === task.totalChunks - 1;

        const uploadPromise = this.uploadChunk(
          task.trackingId!,
          chunkNumber,
          chunk,
          isFinalChunk
        ).then(() => {
          completedChunks++;
          task.currentChunk = completedChunks;
          task.progress = (completedChunks / task.totalChunks) * 100;

      

          // Notify listeners for real-time progress
          this.notifyListeners();
        });

        batchPromises.push(uploadPromise);
      }

      // Wait for all chunks in this batch to complete
      await Promise.all(batchPromises);

      // Save progress after each batch
      this.saveTasksToStorage();

      // Small delay between batches (reduced from 3000ms to 100ms)
      if (batchEnd < task.totalChunks) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    this.saveTasksToStorage();
  }

  private async getChunk(
    file: File,
    chunkNumber: number,
    chunkSize: number
  ): Promise<Blob> {
    const start = chunkNumber * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    return file.slice(start, end);
  }

  private async initiateUpload(task: UploadTask): Promise<UploadResponse> {
    try {
      const response = await post(
        `/api/documents/files/initiate-upload?datasource_id=${task.datasourceId}&group_id=${task.groupId}`,
        {
          filename: task.file.name,
          file_size: task.file.size,
          file_type: this.getFileType(task.file.name),
          chunk_size: this.chunkSize,
          description: task.description || "Uploaded from frontend",
        }
      );

      if (response.data) {
        return response.data;
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error: any) {
      const errorData = error.response?.data || {};
      const detail = errorData.detail;
      const errorMessage = Array.isArray(detail)
        ? detail
            .map((item) =>
              typeof item?.msg === "string" ? item.msg : "Upload validation failed"
            )
            .join(", ")
        : typeof detail === "string"
          ? detail
          : typeof errorData.message === "string"
            ? errorData.message
            : "Failed to initiate upload";

      throw new Error(errorMessage);
    }
  }

  private async uploadChunk(
    trackingId: string,
    chunkNumber: number,
    chunkData: Blob,
    isFinalChunk: boolean
  ): Promise<void> {
    const formData = new FormData();
    formData.append("chunk_file", chunkData);

    // Reduced retry attempts from 3 to 2 for faster failure detection
    const maxRetries = 2;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        await post(
          `/api/documents/files/upload-chunk/${trackingId}?chunk_number=${chunkNumber}&is_final_chunk=${isFinalChunk}`,
          formData
        );

        // Success - exit retry loop
        return;
      } catch (error: any) {
        lastError = error;

        // Don't retry on auth errors or client errors
        if (
          error.response?.status === 401 ||
          error.response?.status === 403 ||
          error.response?.status === 400
        ) {
          throw error;
        }

        // Faster retry with reduced exponential backoff
        if (attempt < maxRetries) {
          const delay = Math.min(500 * Math.pow(2, attempt - 1), 2000); // Max 2 second delay (reduced from 5s)
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    }

    // All retries failed
    throw new Error(
      `Failed to upload chunk ${chunkNumber} after ${maxRetries} attempts: ${lastError?.message}`
    );
  }

  private getFileType(filename: string): string {
    const extension = filename.split(".").pop()?.toLowerCase();
    const typeMap: Record<string, string> = {
      pdf: "PDF",
      txt: "TXT",
      docx: "DOCX",
      csv: "CSV",
      md: "MD",
      mp3: "MP3",
      wav: "WAV",
      mp4: "MP4",
      avi: "AVI",
      mov: "MOV",
    };
    return typeMap[extension || ""] || "UNKNOWN";
  }

  private generateTaskId(): string {
    return `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private saveTasksToStorage(): void {
    try {
      const tasksToSave = Array.from(this.uploadTasks.values()).map((task) => ({
        ...task,
        file: undefined, // Don't save file object to localStorage
        fileInfo: {
          name: task.file.name,
          size: task.file.size,
          type: task.file.type,
        },
      }));
      localStorage.setItem("backgroundUploads", JSON.stringify(tasksToSave));
    } catch (error) {
      console.error("Failed to save upload tasks to storage:", error);
    }
  }

  private loadTasksFromStorage(): void {
    try {
      const saved = localStorage.getItem("backgroundUploads");
      if (saved) {
        const tasks = JSON.parse(saved);
        tasks.forEach((taskData: any) => {
          // Skip tasks that don't have file info or are already completed
          if (!taskData.fileInfo || taskData.status === "completed") {
            return;
          }

          const placeholderFile = new File([""], taskData.fileInfo.name, {
            type: taskData.fileInfo.type,
          });

          const task: UploadTask = {
            ...taskData,
            file: placeholderFile,
            status:
              taskData.status === "uploading" ? "pending" : taskData.status,
            createdAt: new Date(taskData.createdAt),
          };

          this.uploadTasks.set(task.id, task);
        });
      }
    } catch (error) {
      console.error("Failed to load upload tasks from storage:", error);
    }
  }

  private resumePendingUploads(): void {
    // Resume uploads that were in progress when the page was closed
    setTimeout(() => {
      this.processQueue();
    }, 1000); // Small delay to ensure everything is initialized
  }

  private notifyListeners(): void {
    const tasks = this.getAllTasks();
    this.listeners.forEach((callback) => callback(tasks));
  }
}

export default BackgroundUploadService;
